import React, { useState, useEffect } from 'react';
import { collection, onSnapshot, doc, setDoc, deleteDoc, query, orderBy } from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import { Banner } from '../../types';
import { PlusIcon, PhotoIcon } from '../ui/Icons';
import { PencilSquareIcon, TrashIcon } from '../ui/Icons';
import { useNotification } from '../../contexts/AppContext';

const BannersManagementPage: React.FC = () => {
    const [banners, setBanners] = useState<Banner[]>([]);
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingBanner, setEditingBanner] = useState<Banner | null>(null);
    const { showToast } = useNotification();

    // Form state
    const [title, setTitle] = useState('');
    const [imageUrl, setImageUrl] = useState('');
    const [link, setLink] = useState('');
    const [type, setType] = useState<'horizontal' | 'square'>('horizontal');
    const [isActive, setIsActive] = useState(true);
    const [order, setOrder] = useState(0);

    useEffect(() => {
        const q = query(collection(db, 'banners'), orderBy('order', 'asc'));
        const unsubscribe = onSnapshot(q, async (snapshot) => {
            if (snapshot.empty) {
                // Create sample banner if empty
                const sampleBanner: Banner = {
                    id: `banner_sample_${Date.now()}`,
                    title: 'Chào mừng cư dân đến với cổng thông tin HUD3 Linh Đàm',
                    imageUrl: 'https://picsum.photos/seed/hud3/800/400',
                    link: '',
                    type: 'horizontal',
                    isActive: true,
                    order: 0,
                    createdAt: new Date().toISOString(),
                    updatedAt: new Date().toISOString()
                };
                try {
                    await setDoc(doc(db, 'banners', sampleBanner.id), sampleBanner);
                } catch (e) {
                    console.error("Error creating sample banner", e);
                }
            } else {
                const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Banner));
                setBanners(data);
            }
            setLoading(false);
        }, (error) => {
            console.error("Error fetching banners:", error);
            showToast('Lỗi khi tải danh sách banner', 'error');
            setLoading(false);
        });

        return () => unsubscribe();
    }, [showToast]);

    const handleOpenModal = (banner?: Banner) => {
        if (banner) {
            setEditingBanner(banner);
            setTitle(banner.title);
            setImageUrl(banner.imageUrl);
            setLink(banner.link || '');
            setType(banner.type);
            setIsActive(banner.isActive);
            setOrder(banner.order);
        } else {
            setEditingBanner(null);
            setTitle('');
            setImageUrl('');
            setLink('');
            setType('horizontal');
            setIsActive(true);
            setOrder(banners.length);
        }
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setEditingBanner(null);
    };

    const handleSave = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            const id = editingBanner ? editingBanner.id : `banner_${Date.now()}`;
            const bannerData: Banner = {
                id,
                title,
                imageUrl,
                link: link || '',
                type,
                isActive,
                order,
                createdAt: editingBanner ? editingBanner.createdAt : new Date().toISOString(),
                updatedAt: new Date().toISOString()
            };

            await setDoc(doc(db, 'banners', id), bannerData);
            showToast(editingBanner ? 'Cập nhật banner thành công' : 'Thêm banner thành công', 'success');
            handleCloseModal();
        } catch (error) {
            console.error("Error saving banner:", error);
            showToast('Lỗi khi lưu banner', 'error');
        }
    };

    const handleDelete = async (id: string) => {
        if (!window.confirm('Bạn có chắc chắn muốn xóa banner này?')) return;
        try {
            await deleteDoc(doc(db, 'banners', id));
            showToast('Xóa banner thành công', 'success');
        } catch (error) {
            console.error("Error deleting banner:", error);
            showToast('Lỗi khi xóa banner', 'error');
        }
    };

    const handleToggleActive = async (banner: Banner) => {
        try {
            await setDoc(doc(db, 'banners', banner.id), { ...banner, isActive: !banner.isActive, updatedAt: new Date().toISOString() });
            showToast(`Đã ${!banner.isActive ? 'hiển thị' : 'ẩn'} banner`, 'success');
        } catch (error) {
            console.error("Error toggling banner status:", error);
            showToast('Lỗi khi cập nhật trạng thái', 'error');
        }
    };

    if (loading) return <div className="p-6 text-center text-gray-500">Đang tải dữ liệu...</div>;

    return (
        <div className="p-6 max-w-6xl mx-auto space-y-6">
            <div className="flex justify-between items-center bg-white p-4 rounded-xl shadow-sm border">
                <div>
                    <h1 className="text-2xl font-bold text-gray-800">Quản lý Banner Quảng cáo</h1>
                    <p className="text-sm text-gray-500 mt-1">Quản lý các banner hiển thị trên trang chủ cư dân</p>
                </div>
                <button 
                    onClick={() => handleOpenModal()}
                    className="flex items-center gap-2 bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary-dark transition-colors"
                >
                    <PlusIcon className="w-5 h-5" />
                    <span>Thêm Banner</span>
                </button>
            </div>

            <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
                <table className="w-full text-left border-collapse">
                    <thead>
                        <tr className="bg-gray-50 border-b text-sm text-gray-600">
                            <th className="p-4 font-medium">Hình ảnh</th>
                            <th className="p-4 font-medium">Tiêu đề</th>
                            <th className="p-4 font-medium">Loại</th>
                            <th className="p-4 font-medium">Thứ tự</th>
                            <th className="p-4 font-medium">Trạng thái</th>
                            <th className="p-4 font-medium text-right">Thao tác</th>
                        </tr>
                    </thead>
                    <tbody>
                        {banners.length === 0 ? (
                            <tr>
                                <td colSpan={6} className="p-8 text-center text-gray-500">Chưa có banner nào.</td>
                            </tr>
                        ) : (
                            banners.map(banner => (
                                <tr key={banner.id} className="border-b hover:bg-gray-50 transition-colors">
                                    <td className="p-4">
                                        <div className={`bg-gray-100 rounded overflow-hidden flex items-center justify-center ${banner.type === 'horizontal' ? 'w-32 h-16' : 'w-16 h-16'}`}>
                                            {banner.imageUrl ? (
                                                <img src={banner.imageUrl} alt={banner.title} className="w-full h-full object-cover" referrerPolicy="no-referrer" />
                                            ) : (
                                                <PhotoIcon className="w-6 h-6 text-gray-400" />
                                            )}
                                        </div>
                                    </td>
                                    <td className="p-4">
                                        <p className="font-medium text-gray-800">{banner.title}</p>
                                        {banner.link && <a href={banner.link} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-500 hover:underline truncate max-w-xs block">{banner.link}</a>}
                                    </td>
                                    <td className="p-4">
                                        <span className={`px-2 py-1 text-xs rounded-full font-medium ${banner.type === 'horizontal' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}`}>
                                            {banner.type === 'horizontal' ? 'Chữ nhật ngang' : 'Ô vuông'}
                                        </span>
                                    </td>
                                    <td className="p-4 text-gray-600">{banner.order}</td>
                                    <td className="p-4">
                                        <button 
                                            onClick={() => handleToggleActive(banner)}
                                            className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${banner.isActive ? 'bg-green-100 text-green-700 hover:bg-green-200' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                                        >
                                            {banner.isActive ? 'Đang hiển thị' : 'Đang ẩn'}
                                        </button>
                                    </td>
                                    <td className="p-4 text-right space-x-2">
                                        <button onClick={() => handleOpenModal(banner)} className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors" title="Sửa">
                                            <PencilSquareIcon className="w-5 h-5" />
                                        </button>
                                        <button onClick={() => handleDelete(banner.id)} className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors" title="Xóa">
                                            <TrashIcon className="w-5 h-5" />
                                        </button>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {isModalOpen && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl shadow-xl w-full max-w-lg overflow-hidden">
                        <div className="p-6 border-b flex justify-between items-center">
                            <h2 className="text-xl font-bold text-gray-800">{editingBanner ? 'Sửa Banner' : 'Thêm Banner Mới'}</h2>
                            <button onClick={handleCloseModal} className="text-gray-400 hover:text-gray-600">&times;</button>
                        </div>
                        <form onSubmit={handleSave} className="p-6 space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Tiêu đề</label>
                                <input type="text" value={title} onChange={e => setTitle(e.target.value)} required className="w-full border rounded-lg p-2 focus:ring-2 focus:ring-primary/20 outline-none" placeholder="VD: Chào mừng cư dân..." />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">URL Hình ảnh (Hỗ trợ JPG, PNG, GIF)</label>
                                <input type="url" value={imageUrl} onChange={e => setImageUrl(e.target.value)} required className="w-full border rounded-lg p-2 focus:ring-2 focus:ring-primary/20 outline-none" placeholder="https://example.com/banner.gif" />
                                {imageUrl && (
                                    <div className="mt-2 border rounded-lg p-2 bg-gray-50 flex justify-center">
                                        <img src={imageUrl} alt="Preview" className="max-h-32 object-contain" referrerPolicy="no-referrer" onError={(e) => (e.currentTarget.style.display = 'none')} onLoad={(e) => (e.currentTarget.style.display = 'block')} />
                                    </div>
                                )}
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Đường dẫn khi click (Tùy chọn)</label>
                                <input type="url" value={link} onChange={e => setLink(e.target.value)} className="w-full border rounded-lg p-2 focus:ring-2 focus:ring-primary/20 outline-none" placeholder="https://..." />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Loại Banner</label>
                                    <select value={type} onChange={e => setType(e.target.value as 'horizontal' | 'square')} className="w-full border rounded-lg p-2 focus:ring-2 focus:ring-primary/20 outline-none bg-white">
                                        <option value="horizontal">Chữ nhật ngang (2 cột)</option>
                                        <option value="square">Ô vuông (4 cột)</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Thứ tự hiển thị</label>
                                    <input type="number" value={order} onChange={e => setOrder(Number(e.target.value))} required min="0" className="w-full border rounded-lg p-2 focus:ring-2 focus:ring-primary/20 outline-none" />
                                </div>
                            </div>
                            <div className="flex items-center gap-2 mt-2">
                                <input type="checkbox" id="isActive" checked={isActive} onChange={e => setIsActive(e.target.checked)} className="w-4 h-4 text-primary rounded border-gray-300 focus:ring-primary" />
                                <label htmlFor="isActive" className="text-sm font-medium text-gray-700 cursor-pointer">Hiển thị trên trang chủ</label>
                            </div>
                            <div className="pt-4 flex justify-end gap-3">
                                <button type="button" onClick={handleCloseModal} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">Hủy</button>
                                <button type="submit" className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors">{editingBanner ? 'Cập nhật' : 'Thêm mới'}</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default BannersManagementPage;
